<template>
  <v-card class="mb-4 toolbar-card" elevation="1">
    <v-card-text>
      <!-- 上部行: 図面操作 -->
      <v-row align="center">
        <!-- 図面選択ドロップダウン（通常表示） -->
        <v-col v-if="!isEditingName" cols="auto">
          <span class="text-body-2 font-weight-medium">{{ $t('floor_plan_label') }}:</span>
          <v-select
            :value="currentFloorPlan.id"
            :items="floorPlanOptions"
            item-text="name"
            item-value="id"
            dense
            outlined
            hide-details
            class="ml-2 d-inline-block compact-select"
            style="width: 200px;"
            @change="handleFloorPlanChange"
          />
        </v-col>

        <!-- 図面名編集フィールド（編集中） -->
        <v-col v-if="isEditingName" cols="auto">
          <span class="text-body-2 font-weight-medium">{{ $t('floor_plan_label') }}:</span>
          <v-text-field
            v-model="editingName"
            :aria-label="$t('edit_floor_plan_name_aria_label')"
            dense
            outlined
            hide-details
            class="ml-2 d-inline-block"
            style="width: 200px;"
            autofocus
            @keydown.enter="saveNameEdit"
            @keydown.esc="cancelNameEdit"
          />
        </v-col>

        <!-- 編集ボタンエリア -->
        <v-col cols="auto">
          <!-- 通常表示 -->
          <span v-if="!isEditingName">
            <v-btn icon small :aria-label="$t('edit_floor_plan_name')" @click="startNameEdit">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </span>

          <!-- 編集中 -->
          <span v-if="isEditingName">
            <v-btn icon small color="primary" :aria-label="$t('save_floor_plan_name')" @click="saveNameEdit">
              <v-icon>mdi-check</v-icon>
            </v-btn>
          </span>
        </v-col>

        <!-- 図面追加ボタン -->
        <v-col cols="auto">
          <v-btn icon small :aria-label="$t('add_floor_plan')" @click="$emit('add')">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>

        <!-- 図面削除ボタン -->
        <v-col v-if="currentFloorPlan" cols="auto">
          <v-btn
            icon
            small
            :loading="deleting"
            :disabled="deleting"
            :aria-label="$t('delete_floor_plan')"
            @click="$emit('delete')"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-col>

        <v-spacer />

        <!-- 未保存変更メッセージ -->
        <v-col v-if="hasUnsavedChanges && !saving" cols="auto" class="py-0">
          <v-alert type="warning" dense text class="mb-0 pa-1">
            <span class="text-caption">{{ $t('unsaved_changes') }}</span>
          </v-alert>
        </v-col>

        <!-- 保存中メッセージ -->
        <v-col v-if="saving" cols="auto" class="py-0">
          <v-alert type="info" dense text class="mb-0 pa-1">
            <span class="text-caption">{{ $t('saving') }}</span>
          </v-alert>
        </v-col>

        <!-- 保存・取り消しボタン -->
        <v-col v-if="hasUnsavedChanges" cols="auto" class="py-0">
          <v-btn
            color="primary"
            small
            :loading="saving"
            :disabled="saving"
            @click="$emit('save')"
            class="mr-2"
          >
            <v-icon x-small left>mdi-content-save</v-icon>
            {{ $t('save') }}
          </v-btn>
          <v-btn text small @click="$emit('cancel')">
            <v-icon x-small left>mdi-close</v-icon>
            {{ $t('cancel') }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- 下部行: フィルタ -->
      <v-row align="center" class="mt-2">
        <!-- 月選択 -->
        <v-col cols="auto">
          <span class="text-body-2 font-weight-medium">{{ $t('month_label') }}:</span>
          <div class="ml-2 d-inline-block compact-select" style="width: 90px;">
            <month-field
              :value="selectedMonth"
              dense
              outlined
              hide-details
              data-testid="month-selector"
              @input="value => $emit('month-change', value)"
            />
          </div>
        </v-col>

        <!-- 虫種別フィルタ -->
        <v-col cols="auto">
          <span class="text-body-2 font-weight-medium">{{ $t('insect_filter_label') }}:</span>
          <insect-filter-select
            :value="insectFilter"
            class="ml-2 d-inline-block compact-select"
            data-testid="insect-filter-select"
            @input="value => $emit('insect-filter-change', value)"
          />
        </v-col>

        <!-- 描画モード -->
        <v-col cols="auto">
          <span class="text-body-2 font-weight-medium">{{ $t('display_mode_label') }}:</span>
          <v-tooltip bottom max-width="300">
            <template #activator="{ on, attrs }">
              <v-icon
                small
                class="ml-1"
                style="vertical-align: text-bottom"
                data-testid="data-basis-info-icon"
                :aria-label="$t('data_basis_tooltip')"
                v-bind="attrs"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>{{ $t('data_basis_tooltip') }}</span>
          </v-tooltip>
          <v-select
            :value="displayMode"
            :items="displayModeOptions"
            dense
            outlined
            hide-details
            class="ml-2 d-inline-block compact-select"
            style="width: 200px;"
            data-testid="display-mode-select"
            @change="$emit('display-mode-change', $event)"
          />
        </v-col>

        <v-spacer />

        <!-- 図面ダウンロードボタン -->
        <v-col
          v-if="currentFloorPlan"
          cols="auto"
          class="d-flex align-center"
        >
          <v-btn
            icon
            small
            :loading="exporting"
            :disabled="exporting"
            :aria-label="$t('download_floor_plan')"
            @click="$emit('download')"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    <v-progress-linear
      v-if="loadingEvents || exporting"
      indeterminate
      color="primary"
      height="2"
      class="toolbar-progress"
    />
  </v-card>
</template>

<i18n lang="yaml">
ja:
  floor_plan_label: 図面
  month_label: 表示月
  insect_filter_label: 虫種別
  display_mode_label: 描画モード
  data_basis_tooltip: 大きさ・色の変化は、過去1年間の捕獲データをもとに算出しています。
  display_mode_placement_only: 配置のみ
  display_mode_none: 表示なし
  display_mode_name: トラップ名のみ
  display_mode_name_and_count: トラップ名+捕獲数
  display_mode_heat: ヒートマップ
  display_mode_comparison_heat: ヒートマップ（前月比較）
  unsaved_changes: 未保存の変更があります
  saving: 保存中...
  save: 保存
  cancel: 取り消し
  add_floor_plan: 図面を追加
  delete_floor_plan: 図面を削除
  no_floor_plan: 図面なし
  edit_floor_plan_name: 図面名を編集
  save_floor_plan_name: 図面名を保存
  edit_floor_plan_name_aria_label: 図面名
  download_floor_plan: 図面を画像でダウンロード

en:
  floor_plan_label: Floor Plan
  month_label: Month
  insect_filter_label: Insect Type
  display_mode_label: Display Mode
  data_basis_tooltip: Size and color changes are calculated based on the past year of capture data.
  display_mode_placement_only: Placement only
  display_mode_none: None
  display_mode_name: Name only
  display_mode_name_and_count: Name + Count
  display_mode_heat: Heatmap
  display_mode_comparison_heat: Heatmap (vs Previous Month)
  unsaved_changes: You have unsaved changes
  saving: Saving...
  save: Save
  cancel: Cancel
  add_floor_plan: Add floor plan
  delete_floor_plan: Delete floor plan
  no_floor_plan: No floor plan
  edit_floor_plan_name: Edit floor plan name
  save_floor_plan_name: Save floor plan name
  edit_floor_plan_name_aria_label: Floor Plan Name
  download_floor_plan: Download floor plan as image
</i18n>

<script>
import MonthField from '@/components/molecules/MonthField';
import InsectFilterSelect from '@/components/molecules/InsectFilterSelect';
import Features from '@/mixins/features';
import { DISPLAY_MODES } from './displayModes';

export default {
  name: 'FloorPlanToolbar',
  components: {
    MonthField,
    InsectFilterSelect,
  },
  mixins: [Features],
  props: {
    currentFloorPlan: {
      type: Object,
      required: true,
    },
    allFloorPlans: {
      type: Array,
      default: () => [],
    },
    selectedMonth: {
      type: String,
      required: true,
    },
    insectFilter: {
      type: String,
      default: 'all',
    },
    displayMode: {
      type: String,
      default: DISPLAY_MODES.NONE,
      validator: (value) => Object.values(DISPLAY_MODES).includes(value),
    },
    loadingEvents: {
      type: Boolean,
      default: false,
    },
    hasUnsavedChanges: {
      type: Boolean,
      default: false,
    },
    deleting: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    exporting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isEditingName: false,
      editingName: '',
    };
  },
  computed: {
    floorPlanOptions() {
      return this.allFloorPlans.map((fp) => ({
        id: fp.id,
        name: fp.name,
      }));
    },
    displayModeOptions() {
      const options = [
        { text: this.$t('display_mode_placement_only'), value: DISPLAY_MODES.PLACEMENT_ONLY },
        { text: this.$t('display_mode_none'), value: DISPLAY_MODES.NONE },
        { text: this.$t('display_mode_name'), value: DISPLAY_MODES.NAME },
        { text: this.$t('display_mode_name_and_count'), value: DISPLAY_MODES.NAME_AND_COUNT },
      ];
      options.push({ text: this.$t('display_mode_heat'), value: DISPLAY_MODES.HEAT });
      options.push({ text: this.$t('display_mode_comparison_heat'), value: DISPLAY_MODES.COMPARISON_HEAT });
      return options;
    },
  },
  watch: {
    currentFloorPlan(newVal, oldVal) {
      // 図面IDが変わった場合（削除/アップロード）、編集状態をクリア
      if (oldVal && newVal?.id !== oldVal?.id) {
        this.isEditingName = false;
        this.editingName = '';
      }
    },
  },
  methods: {
    handleFloorPlanChange(floorPlanId) {
      this.$emit('change', floorPlanId);
    },
    startNameEdit() {
      this.editingName = this.currentFloorPlan.name;
      this.isEditingName = true;
    },
    saveNameEdit(event) {
      // IME入力中は処理しない
      if (event && event.isComposing) {
        return;
      }

      const trimmedName = this.editingName.trim();

      // 空文字の場合はキャンセル扱い
      if (!trimmedName) {
        this.cancelNameEdit();
        return;
      }

      // 名前が変わっていない場合はキャンセル扱い
      if (trimmedName === this.currentFloorPlan.name) {
        this.cancelNameEdit();
        return;
      }

      // 親コンポーネントに保存イベントを送信
      this.$emit('save-name', trimmedName);

      // 編集モードを終了
      this.isEditingName = false;
    },
    cancelNameEdit() {
      this.isEditingName = false;
      this.editingName = '';
    },
  },
};
</script>

<style scoped>
.compact-select {
  font-size: 13px;
}

.compact-select ::v-deep .v-input__slot {
  min-height: 32px !important;
}

.compact-select ::v-deep .v-input__append-inner {
  margin-top: 5px !important;
}

.compact-select ::v-deep .v-select__selection,
.compact-select ::v-deep .v-text-field__slot input {
  font-size: 13px;
  text-align: center;
}

.toolbar-card {
  position: relative;
}

.toolbar-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
